import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";

// If your styles.css is at the REPO ROOT (not /src), keep this:
import "../styles.css";

import { auth } from "@/firebase";
import {
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
} from "firebase/auth";

/**
 * Views your app supports (used to normalize older routes).
 * This helps prevent blank pages when someone hits /documents instead of /location/:id/documents
 */
const VALID_VIEWS = new Set([
  "dashboard",
  "clients",
  "case_detail",
  "documents",
  "settings",
  "account",
  "login",
  "demo-login",
  "sso",
  "crm-sso",
  "sso-launch",
  "crm-callback",
  "privacy",
  "terms",
]);

const getPathSegments = () => window.location.pathname.split("/").filter(Boolean);

const safeSessionGet = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const resolveTenantIdFast = (): string | null => {
  const seg = getPathSegments();
  const search = new URLSearchParams(window.location.search);

  return (
    (seg[0] === "location" ? seg[1] : null) ||
    search.get("locationId") ||
    search.get("staff_context") ||
    safeSessionGet("FILERSHUB_SSO_LOC")
  );
};

const dispatchNavigate = () => {
  try {
    window.dispatchEvent(new Event("filershub:navigate"));
  } catch {
    // ignore
  }
};

const normalizeHashRoute = () => {
  const raw = (window.location.hash || "").replace("#", "").trim().toLowerCase();
  if (!raw || !VALID_VIEWS.has(raw)) return;

  const tenantId = resolveTenantIdFast();
  const search = window.location.search;

  // Don’t rewrite legal pages
  if (raw === "privacy" || raw === "terms") {
    window.history.replaceState({}, "", `/${raw}${search}`);
    dispatchNavigate();
    return;
  }

  // If we have a tenant, convert #documents -> /location/:id/documents
  if (tenantId) {
    const nextUrl = `/location/${tenantId}/${raw}${search}`;
    window.history.replaceState({}, "", nextUrl);
    dispatchNavigate();
    return;
  }

  // No tenant yet, drop the hash so it stops sticking
  window.history.replaceState({}, "", window.location.pathname + search);
  dispatchNavigate();
};

const normalizeBareViewPath = () => {
  const seg = getPathSegments();
  if (seg.length === 0) return;

  const first = (seg[0] || "").toLowerCase();

  // Never touch legal pages
  if (first === "privacy" || first === "terms") return;

  // Already tenant-scoped
  if (first === "location") return;

  // If it isn't a known view, leave it alone (could be /api, etc.)
  if (!VALID_VIEWS.has(first)) return;

  const tenantId = resolveTenantIdFast();
  if (!tenantId) return;

  const search = window.location.search;
  const nextUrl = `/location/${tenantId}/${first}${search}`;

  window.history.replaceState({}, "", nextUrl);
  dispatchNavigate();
};

const setBestAuthPersistence = async () => {
  // In iframes, local/session storage can be blocked.
  // Fall back to in-memory persistence so auth still works.
  try {
    await setPersistence(auth, browserLocalPersistence);
    return "local";
  } catch {}

  try {
    await setPersistence(auth, browserSessionPersistence);
    return "session";
  } catch {}

  await setPersistence(auth, inMemoryPersistence);
  return "memory";
};

// Run normalizers BEFORE React boots
normalizeHashRoute();
normalizeBareViewPath();

const start = async () => {
  // Make auth iframe-safe before any listeners mount
  try {
    const mode = await setBestAuthPersistence();
    console.log("[bootstrap] auth persistence:", mode);
  } catch (e) {
    console.warn("[bootstrap] persistence setup failed, continuing:", e);
  }

  const container = document.getElementById("root");
  if (!container) {
    console.error("Missing #root element in index.html");
    return;
  }

  createRoot(container).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
};

void start();
